/**
 * Client API REST — point d'entrée unique pour les appels backend.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'hg_token';

/**
 * Lot C — base du service synotis-core (auth centralisée). Le login redirige
 * directement vers le core. `VITE_CORE_BASE_URL` configure la base (défaut dev).
 */
const CORE_BASE_URL = import.meta.env.VITE_CORE_BASE_URL || 'http://localhost:4100';

export function loginUrl(next = '/') {
  const url = new URL('/auth/google', CORE_BASE_URL);
  url.searchParams.set('origin', window.location.origin);
  url.searchParams.set('next', next);
  return url.toString();
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Lit la valeur d'un cookie lisible (non-HttpOnly) côté navigateur.
 * Utilisé pour le token CSRF SSO (`syn_csrf`) posé par synotis-core.
 */
function readCookie(name) {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)'),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

// SC-2 — les cookies SSO sont suffixés `_stg` en staging (slot distinct sur
// `.synotis.tech`). Le nom du cookie CSRF lu côté front dépend donc du host :
// un visiteur peut avoir À LA FOIS `syn_csrf` (prod) et `syn_csrf_stg` (staging)
// dans son navigateur → on choisit selon le hostname, pas par simple présence.
const IS_STAGING =
  typeof window !== 'undefined' && window.location.hostname.includes('-staging.');
const CSRF_COOKIE_NAME = IS_STAGING ? 'syn_csrf_stg' : 'syn_csrf';
const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  // Lot C — la session normale passe par le cookie SSO (`credentials:include`).
  // On n'envoie le Bearer QUE si un token local existe (voie legacy).
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  // Lot D — CSRF double-submit : sur les mutations, renvoie le token du cookie
  // `syn_csrf` en en-tête `X-CSRF-Token` (la voie Bearer en est exemptée côté
  // serveur, mais l'envoyer ne nuit pas).
  if (MUTATION_METHODS.has(method.toUpperCase())) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    throw new Error('Non authentifié');
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

async function upload(path, fileOrFiles, fieldName = 'file', extraFields = {}) {
  const fd = new FormData();
  // Accepte un fichier unique ou une liste (multi-import CRA) ; le backend lit
  // tous les fichiers via multer `.any()`.
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  for (const f of files) fd.append(fieldName, f);
  for (const [k, v] of Object.entries(extraFields)) fd.append(k, String(v));
  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  // Lot D — POST mutant : CSRF double-submit pour la voie cookie SSO.
  const csrf = readCookie(CSRF_COOKIE_NAME);
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: fd,
  });
  if (res.status === 401) {
    clearToken();
    throw new Error('Non authentifié');
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  health: () => request('GET', '/health'),

  auth: {
    /**
     * Lot C — login via SSO core (remplace l'OAuth Google local). On redirige le
     * navigateur vers `${CORE_BASE_URL}/auth/google?origin=<origin>&next=…` ; le
     * core authentifie, pose `syn_session` + `syn_csrf` sur `.synotis.tech`, puis
     * renvoie sur `origin + next`.
     */
    loginUrl,
    /** Conservé pour bascule d'urgence vers l'OAuth Google LOCAL (dormant). */
    googleLoginUrl: (next = '/') =>
      `${API_URL}/auth/google?next=${encodeURIComponent(next)}`,
    me: () => request('GET', '/auth/me'),
    logout: () => request('POST', '/auth/logout'),
  },

  forecast: {
    /**
     * Calcule le forecast pour une période donnée.
     * @param {object} params
     * @param {string} params.kind - "next-week" | "rolling-month" | "next-month" | "rolling-3-months" | "next-quarter" | "custom"
     * @param {string} [params.start] - YYYY-MM-DD (custom uniquement)
     * @param {string} [params.end] - YYYY-MM-DD (custom uniquement)
     * @param {boolean} [params.snapshot] - persiste un ForecastSnapshot côté serveur
     */
    compute: ({ kind, start, end, snapshot } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      if (snapshot) qs.set('snapshot', 'true');
      return request('GET', `/forecast?${qs}`);
    },
    listSnapshots: ({ limit = 20 } = {}) =>
      request('GET', `/forecast/snapshots?limit=${limit}`),

    /**
     * Diagnostic détaillé d'un collaborateur pour une période donnée.
     * Retourne capacité, allocations, tasks brutes Float jour par jour.
     */
    debugPerson: (peopleId, { kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/forecast/people/${peopleId}/debug?${qs}`);
    },

    /**
     * Série temporelle hebdomadaire d'une personne (% billable + % non-billable
     * par semaine) sur la période demandée.
     *
     * @param {object} opts
     * @param {boolean} [opts.prospection] - fusionne la couche prospection (lignes à la mer)
     * @param {boolean} [opts.weighted] - si prospection=true, applique × proba/100
     */
    personTimeline: (peopleId, { kind, start, end, prospection, weighted } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      if (prospection) qs.set('prospection', 'true');
      if (weighted) qs.set('weighted', 'true');
      return request('GET', `/forecast/people/${peopleId}/timeline?${qs}`);
    },

    /** Tableau des prospections actives d'un consultant (hors fenêtre temporelle). */
    personProspections: (peopleId) =>
      request('GET', `/forecast/people/${peopleId}/prospections`),

    /** Pose ou met à jour un override manuel de taux pour une personne. */
    setOverride: (peopleId, rate) =>
      request('PUT', `/forecast/people/${peopleId}/override`, { rate }),

    /** Retire l'override manuel (retour à l'auto-détection). */
    clearOverride: (peopleId) =>
      request('DELETE', `/forecast/people/${peopleId}/override`),

    /** Masque la personne de la liste principale. */
    hidePerson: (peopleId) =>
      request('PUT', `/forecast/people/${peopleId}/hide`),

    /** Rend la personne à nouveau visible. */
    showPerson: (peopleId) =>
      request('DELETE', `/forecast/people/${peopleId}/hide`),

    /** Pose la cible billable manuelle pour une personne (0..1.5). */
    setBillableTarget: (peopleId, target) =>
      request('PUT', `/forecast/people/${peopleId}/billable-target`, { target }),

    /** Retire la cible manuelle (retour à 100 % par défaut). */
    clearBillableTarget: (peopleId) =>
      request('DELETE', `/forecast/people/${peopleId}/billable-target`),
  },

  prospect: {
    /** Upload du CSV d'export CRM. Renvoie le rapport d'import. */
    importCsv: (file) => upload('/prospect/import', file),

    /** Liste des propositions (filtrées hors `excluded`). */
    list: ({ status, needsStaffing } = {}) => {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (needsStaffing != null) qs.set('needsStaffing', String(needsStaffing));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/prospect/proposals${suffix}`);
    },

    /** Détail d'une proposition (assignments + weeks). */
    detail: (crmId) => request('GET', `/prospect/proposals/${crmId}`),

    /** Analyse des offres (bandeau Prospection, même agrégat que la vue Direction). */
    offersAnalysis: () => request('GET', '/prospect/offers-analysis'),

    /** Photos trimestrielles de l'analyse des offres et leur devenir (#15). */
    offersHistory: () => request('GET', '/prospect/offers-history'),

    /**
     * Met à jour le statut d'une proposition (édition utilisateur).
     * Si status passe à "Perdu" → on peut saisir lostReason + lostPostmortemUrl.
     * Si status quitte "Perdu" → l'API nettoie automatiquement ces deux champs.
     */
    updateProposal: (crmId, { status, probability, lostReason, lostPostmortemUrl } = {}) =>
      request('PATCH', `/prospect/proposals/${crmId}`, {
        ...(status !== undefined ? { status } : {}),
        ...(probability !== undefined ? { probability } : {}),
        ...(lostReason !== undefined ? { lostReason } : {}),
        ...(lostPostmortemUrl !== undefined ? { lostPostmortemUrl } : {}),
      }),

    /** Historique des imports CSV. */
    batches: () => request('GET', '/prospect/batches'),

    /** Historique d'audit d'une proposition (statut, proba, tags). */
    proposalAudit: (crmId) => request('GET', `/prospect/proposals/${crmId}/audit`),
    /** Interlocuteur Gescom de la proposition (synocore) : `{ contact | null }`. */
    proposalContact: (crmId) => request('GET', `/prospect/proposals/${crmId}/contact`),

    // ---------- Affaire d'avant-vente (AVV) ----------

    /**
     * Prépare la modale de création AVV : libellé proposé + clients Float
     * candidats (match exact + approximatif sur le client de la prospection).
     */
    avvClientMatch: (crmId) =>
      request('GET', `/prospect/proposals/${crmId}/avv/client-match`),

    /**
     * Crée l'affaire d'avant-vente (projet Float non billable + rattachement staff).
     * @param {number} crmId
     * @param {object} input
     * @param {string} input.name - Libellé du projet AVV
     * @param {string|null} [input.coreClientId] - Client du référentiel central
     * @param {string|null} [input.createClientName] - Nom de client à créer dans le référentiel central (exclusif avec coreClientId)
     */
    createAvv: (crmId, { coreClientId = null, name, createClientName = null } = {}) =>
      request('POST', `/prospect/proposals/${crmId}/avv`, {
        name,
        ...(coreClientId != null ? { coreClientId } : {}),
        ...(createClientName ? { createClientName } : {}),
      }),

    /**
     * Recherche libre dans le référentiel client **central** (synocore).
     * 3e étage de la résolution : indispensable, une majorité des clients de
     * prospection portent un code Gescom encore inconnu du maître.
     */
    searchClients: (q) =>
      request('GET', `/prospect/clients/search?q=${encodeURIComponent(q)}`),

    // ---------- Affaire facturable Synolog (proba ≥ 75) ----------

    /**
     * Récapitulatif avant création : nom, code, clients candidats, budget CHF,
     * TJM moyen, équipe résolue, garde-fous bloquants (`blockers`).
     */
    synologAffairePreview: (crmId) =>
      request('GET', `/prospect/proposals/${crmId}/synolog-affaire/preview`),

    /** Devis de la proposition : `{ quote }` (null si aucun devis généré). */
    quote: (crmId) => request('GET', `/prospect/proposals/${crmId}/quote`),

    /**
     * Génère le devis, ou le REGÉNÈRE en écrasant le fichier Drive (même numéro,
     * version +1) et en renvoyant le mail. Réponse : `{ quote, ok, regenerated, mail }`.
     */
    generateQuote: (crmId, contactChoice) =>
      request('POST', `/prospect/proposals/${crmId}/quote`, contactChoice ? { contactChoice } : undefined),

    /**
     * Interlocuteurs possibles pour le devis : `{ items, crmDefault, crmDefaultName, current, reason }`.
     * `contactChoice` à passer à `generateQuote` : `{ mode: 'crm' }`, `{ mode: 'none' }` ou
     * `{ mode: 'contact', gescomContactId }`.
     */
    quoteContacts: (crmId) => request('GET', `/prospect/proposals/${crmId}/quote/contacts`),

    /**
     * Crée l'affaire facturable dans Synolog (+ équipe + allocations).
     * @param {number} crmId
     * @param {object} input - exactement un des deux champs
     * @param {string|null} [input.coreClientId] - Client du référentiel central (synocore)
     * @param {string|null} [input.createClientName] - Nom de client à créer
     * @param {boolean} [input.createAllocations] - Plannings issus du staffing (opt-in)
     * @param {string} [input.budgetType] - NONE | HOURLY_FEE | FEE_PER_PROJECT | FEE_PER_PHASE
     * @param {{peopleId:number,tjm:number}[]} [input.tjmOverrides] - TJM négociés (ADMIN)
     */
    createSynologAffaire: (
      crmId,
      { coreClientId = null, createClientName = null, createAllocations = false, budgetType, tjmOverrides } = {},
    ) =>
      request('POST', `/prospect/proposals/${crmId}/synolog-affaire`, {
        ...(coreClientId != null ? { coreClientId } : {}),
        ...(createClientName ? { createClientName } : {}),
        // ⚠ Transmis explicitement : l'ancienne signature ne relayait que le
        // client, la bascule « Plannings » de la modale était sans effet.
        createAllocations: createAllocations === true,
        ...(budgetType ? { budgetType } : {}),
        ...(tjmOverrides?.length ? { tjmOverrides } : {}),
      }),

    /**
     * Dashboard Avant-Vente : charge pointée (logged-time sur projets AVV) vs
     * jours de prestation prévus, par affaire. Fenêtre par défaut côté serveur :
     * 180 derniers jours.
     */
    avvDashboard: ({ start, end } = {}) => {
      const qs = new URLSearchParams();
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/prospect/avv/dashboard${suffix}`);
    },

    /** Autocomplete consultants pour ajout manuel à une proposition. */
    consultants: ({ q = '', onlyFloat = true } = {}) => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      qs.set('onlyFloat', String(onlyFloat));
      return request('GET', `/prospect/consultants?${qs}`);
    },

    /** Ajoute un consultant à une proposition. */
    addAssignment: (crmId, { peopleId, caPercent = null }) =>
      request('POST', `/prospect/proposals/${crmId}/assignments`, { peopleId, caPercent }),

    /** Supprime un assignment (et ses weeks en cascade). */
    deleteAssignment: (assignmentId) =>
      request('DELETE', `/prospect/assignments/${assignmentId}`),

    /** Met à jour le % CA d'un assignment (null = non défini). */
    updateAssignmentCaPercent: (assignmentId, caPercent) =>
      request('PATCH', `/prospect/assignments/${assignmentId}`, { caPercent }),

    /**
     * Saisit la charge hebdo uniforme d'un assignment sur une plage [start, end].
     * @param {string} assignmentId
     * @param {object} input
     * @param {string} input.start - YYYY-MM-DD
     * @param {string} input.end - YYYY-MM-DD
     * @param {'daysPerWeek'|'occupationPercent'} input.mode
     * @param {number} input.value
     * @param {boolean} [input.replaceAll]
     */
    setUniformWeeks: (assignmentId, input) =>
      request('PUT', `/prospect/assignments/${assignmentId}/weeks`, input),

    /**
     * « Répartition auto » : déduit les jours-homme du budget (montant / TJM) et
     * les étale sur la plage (plafond plein temps tenant compte du temps partiel,
     * jamais moins d'une demi-journée par semaine staffée). Écrase l'existant.
     * @param {string} assignmentId
     * @param {object} input
     * @param {string} input.start - YYYY-MM-DD
     * @param {string} input.end - YYYY-MM-DD
     * @param {number} input.amount - part du budget revenant au consultant (CHF)
     * @param {number} input.tjm - TJM effectif du consultant (CHF)
     * @param {number|null} [input.etpPercent] - temps partiel (100/80/…)
     * @param {'daysPerWeek'|'occupationPercent'} input.mode
     */
    distributeAuto: (assignmentId, input) =>
      request('POST', `/prospect/assignments/${assignmentId}/distribute`, input),

    /**
     * « Répartir le budget » (#20) : partage le montant de la proposition entre
     * les consultants staffés (part CA, sinon parts égales), convertit chaque part
     * en jours avec le TJM Gescom et l'étale sur la plage. Écrase l'existant.
     * @param {number} crmId
     * @param {{ start: string, end: string, mode: 'daysPerWeek'|'occupationPercent' }} input
     * @returns {Promise<{ amount: number, lines: Array<{ assignmentId: string, personName: string, sharePercent: number, shareAmount: number, tjm: number|null, totalDays: number|null, weeksCount: number, skipped: string|null }> }>}
     */
    distributeTeam: (crmId, input) =>
      request('POST', `/prospect/proposals/${crmId}/distribute`, input),

    /** Supprime une semaine spécifique. */
    deleteWeek: (assignmentId, weekStart) =>
      request('DELETE', `/prospect/assignments/${assignmentId}/weeks/${weekStart}`),

    // ---------- Tags technologiques ----------

    /** Liste tous les tags avec leur compteur d'utilisation. */
    listTags: () => request('GET', '/prospect/tech-tags'),

    /** Recherche de tags proches (doublons orthographiques) — Levenshtein ≤ 2. */
    suggestTags: (q) =>
      request('GET', `/prospect/tech-tags/suggest?q=${encodeURIComponent(q)}`),

    /** Crée (ou retourne) un tag pour le nom donné. */
    createTag: (name) => request('POST', '/prospect/tech-tags', { name }),

    /**
     * Remplace les tags d'une proposition.
     * Body : { tagIds: number[], tagNames?: string[] (créés si inconnus) }
     */
    setProposalTags: (crmId, { tagIds, tagNames } = {}) =>
      request('PUT', `/prospect/proposals/${crmId}/tech-tags`, {
        tagIds: tagIds ?? [],
        tagNames: tagNames ?? [],
      }),
  },

  admin: {
    // ---------- Référentiel client central (synocore) ----------
    //
    // Aucune table locale : cet écran lit et écrit DIRECTEMENT le maître.
    // Toute écriture renvoie `{ client, replication: { ok, error, retryable } }`
    // — une réplication synolog ratée ne défait jamais l'écriture centrale.
    // `retryable: false` = refus de synolog (409) : *Réessayer* ne peut pas
    // aboutir, il faut un arbitrage humain dans synolog.
    clients: {
      list: ({ q, archived, cursor, limit } = {}) => {
        const qs = new URLSearchParams();
        if (q) qs.set('q', q);
        if (archived) qs.set('archived', archived);
        if (cursor) qs.set('cursor', cursor);
        if (limit) qs.set('limit', String(limit));
        const suffix = qs.toString() ? `?${qs}` : '';
        return request('GET', `/admin/clients${suffix}`);
      },
      create: ({ name, gescomId = null }) =>
        request('POST', '/admin/clients', { name, ...(gescomId ? { gescomId } : {}) }),
      /** Renommage interdit sur un client `gescom_csv` (refusé côté serveur). */
      update: (id, patch) => request('PATCH', `/admin/clients/${encodeURIComponent(id)}`, patch),
      archive: (id) => request('DELETE', `/admin/clients/${encodeURIComponent(id)}`),
      restore: (id) =>
        request('PATCH', `/admin/clients/${encodeURIComponent(id)}`, { active: true }),
      /** Bouton « Réessayer » : relance la seule réplication vers synolog. */
      replicate: (id) => request('POST', `/admin/clients/${encodeURIComponent(id)}/replicate`),
    },

    // ---------- Interlocuteurs client (contacts Gescom, maître synocore) ----------
    //
    // Aucun stockage local. `simulate` renvoie le rapport + `hash` ; `apply`
    // exige ce même `hash` (le serveur refuse en 409 un fichier non simulé).
    contacts: {
      simulate: (file) => upload('/admin/contacts/import', file, 'file', { apply: 'false' }),
      apply: (file, expectedHash) =>
        upload('/admin/contacts/import', file, 'file', { apply: 'true', expectedHash }),
      list: ({ gescomId, status } = {}) => {
        const qs = new URLSearchParams();
        if (gescomId) qs.set('gescomId', gescomId);
        if (status) qs.set('status', status);
        const suffix = qs.toString() ? `?${qs}` : '';
        return request('GET', `/admin/contacts${suffix}`);
      },
    },

    /** Synchronise ConsultantMapping depuis le schema `technical`. */
    refreshConsultantMapping: () =>
      request('POST', '/admin/refresh-consultant-mapping'),
    listConsultantMapping: () => request('GET', '/admin/consultant-mapping'),

    // ---------- Tarifs (CJM / TJM) ----------

    /** Liste des tarifs collaborateurs avec TJM effectif et coef marge courant. */
    listRates: ({ bu, missingCjm, missingTjm } = {}) => {
      const qs = new URLSearchParams();
      if (bu) qs.set('bu', bu);
      if (missingCjm) qs.set('missingCjm', 'true');
      if (missingTjm) qs.set('missingTjm', 'true');
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/admin/rates${suffix}`);
    },

    /** Upload du CSV "rapport collaborateur ↔ CJM". */
    importRates: (file) => upload('/admin/rates/import', file),

    /**
     * Met à jour le CJM et/ou le TJM override d'un collaborateur (en CHF).
     * Passer `null` pour effacer un champ, `undefined` pour le laisser intact.
     */
    updateRate: (smileId, { cjm, tjmOverride } = {}) => {
      const body = {};
      if (cjm !== undefined) body.cjm = cjm;
      if (tjmOverride !== undefined) body.tjmOverride = tjmOverride;
      return request('PATCH', `/admin/rates/${smileId}`, body);
    },

    /** @deprecated — utiliser `updateRate(smileId, { tjmOverride })`. */
    updateRateTjm: (smileId, tjmOverride) =>
      request('PATCH', `/admin/rates/${smileId}`, { tjmOverride }),

    /**
     * Pousse les tarifs (CJM/TJM) des collaborateurs sélectionnés vers Float.
     * Avec `dryRun: true`, renvoie le plan (aperçu) sans rien écrire.
     * @param {number[]} smileIds
     * @param {{ dryRun?: boolean }} [opts]
     */

    /** Soft delete d'un collaborateur (masqué des listes, préservé en BDD). */
    deleteRate: (smileId) => request('DELETE', `/admin/rates/${smileId}`),

    /** Restore d'un collaborateur soft-deleted. */
    restoreRate: (smileId) => request('POST', `/admin/rates/${smileId}/restore`),

    /**
     * Paramètres globaux : tarifs (marginCoefficient, eurToChfRate) + seuils de
     * la vue Direction (dryUrgencyThresholdsMonths, offersHorizonMonths,
     * offersDateField, offersAmountBuckets) + seuils de charge billable
     * (underloadThresholdPct, moderateLoadUpperPct).
     */
    getSettings: () => request('GET', '/admin/settings'),
    /** Chrono annuel des devis : `{ year, nextChrono, configured, lastAllocated, lastCrmNumber, nextNumberPreview }`. */
    getQuoteSequence: (year) => request('GET', `/admin/quotes/sequence?year=${encodeURIComponent(year)}`),
    /** Paramètre le prochain chrono (1–9999) ; 409 si ≤ dernier attribué ; `warning` éventuel. */
    setQuoteSequence: (year, nextChrono) => request('PUT', '/admin/quotes/sequence', { year, nextChrono }),
    /** Dossier Drive racine des devis : `{ effectiveFolderId, source, serviceAccountEmail, access }` (+ test d'accès). */
    getQuoteDrive: () => request('GET', '/admin/quotes/drive'),
    /** Enregistre le dossier racine (id, URL Drive, ou '' = retour à l'env) ; 409 si inaccessible. */
    setQuoteDrive: (folderId) => request('PUT', '/admin/quotes/drive', { folderId }),
    /**
     * Maj partielle des settings. Envoyer uniquement les champs à modifier.
     * @param {{
     *   marginCoefficient?: number,
     *   eurToChfRate?: number,
     *   dryUrgencyThresholdsMonths?: number[],
     *   offersHorizonMonths?: number,
     *   offersDateField?: 'productionStart'|'remiseDate'|'endActionDate',
     *   offersAmountBuckets?: number[],
     *   underloadThresholdPct?: number,
     *   moderateLoadUpperPct?: number,
     * }} updates
     */
    updateSettings: (updates) => request('PATCH', '/admin/settings', updates),
    /** @deprecated — utiliser `updateSettings({ marginCoefficient })`. */
    updateMarginCoefficient: (marginCoefficient) =>
      request('PATCH', '/admin/settings', { marginCoefficient }),

    // ---------- Utilisateurs (gestion des rôles) ----------

    /**
     * Liste des utilisateurs avec filtres optionnels.
     * @param {{ status?: 'PENDING'|'ACTIVE'|'DISABLED', role?: 'ADMIN'|'USER'|'VIEWER' }} filters
     * @returns {Promise<{ items: Array, pendingCount: number }>}
     */
    listUsers: ({ status, role } = {}) => {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (role) qs.set('role', role);
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/admin/users${suffix}`);
    },

    /**
     * Met à jour le rôle, le statut, l'accès Direction et/ou le persona manager
     * d'un utilisateur. Au moins l'un des champs doit être fourni.
     */
    updateUser: (id, { role, status, canViewExec, isManager } = {}) => {
      const body = {};
      if (role !== undefined) body.role = role;
      if (status !== undefined) body.status = status;
      if (canViewExec !== undefined) body.canViewExec = canViewExec;
      if (isManager !== undefined) body.isManager = isManager;
      return request('PATCH', `/admin/users/${id}`, body);
    },

    // ---------- Audit / Activité ----------

    /**
     * Statistiques agrégées d'audit sur une période.
     * @param {{ days?: number, from?: string, to?: string }} params
     * @returns {Promise<{ kpis, loginsByRole, mutationsByAction, activityByUser, daily }>}
     */
    auditStats: ({ days, from, to } = {}) => {
      const qs = new URLSearchParams();
      if (days != null) qs.set('days', String(days));
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/admin/audit/stats${suffix}`);
    },

    /**
     * Journal d'audit paginé et filtrable.
     * @param {{ days?: number, from?: string, to?: string, userId?: string,
     *   action?: string, category?: 'login'|'mutation'|'all',
     *   limit?: number, offset?: number }} params
     * @returns {Promise<{ items: Array, total: number, hasMore: boolean }>}
     */
    auditLogs: ({ days, from, to, userId, action, category, limit, offset } = {}) => {
      const qs = new URLSearchParams();
      if (days != null) qs.set('days', String(days));
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (userId) qs.set('userId', userId);
      if (action) qs.set('action', action);
      if (category && category !== 'all') qs.set('category', category);
      if (limit != null) qs.set('limit', String(limit));
      if (offset != null) qs.set('offset', String(offset));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request('GET', `/admin/audit/logs${suffix}`);
    },

    // ---------- Modèles d'email (rappels Float éditables) ----------
    mailTemplates: {
      /** Catalogue des modèles : effectif (surcharge BDD ou défaut intégré) + métadonnées. */
      list: () => request('GET', '/admin/mail-templates'),

      /** Enregistre la surcharge d'un modèle. */
      save: (key, { subject, bodyHtml }) =>
        request('PUT', `/admin/mail-templates/${key}`, { subject, bodyHtml }),

      /** Réinitialise un modèle au défaut intégré (supprime la surcharge). */
      reset: (key) => request('DELETE', `/admin/mail-templates/${key}`),

      /**
       * Aperçu rendu avec des données d'exemple. `draft` permet de prévisualiser
       * un brouillon non enregistré : { subject?, bodyHtml? }.
       */
      preview: (key, draft = {}) =>
        request('POST', `/admin/mail-templates/${key}/preview`, draft),
    },
  },

  // ---------- Pilotage BL (#21, réservé canViewExec ; mapping ADMIN) ----------
  pilotageBl: {
    /** Activité de la période + même période N-1 (lignes personne × mois, détail par affaire). */
    activity: ({ start, end }) =>
      request('GET', `/pilotage-bl/activity?${new URLSearchParams({ start, end })}`),
    /** Planifié (atterrissage de l'année, #23) — lignes personne × mois. */
    forecast: ({ start, end }) =>
      request('GET', `/pilotage-bl/forecast?${new URLSearchParams({ start, end })}`),
    /** Propositions avec BL résolue par l'offre. */
    commercial: () => request('GET', '/pilotage-bl/commercial'),
    /** Réglage offre → BL (ADMIN). */
    offerMapping: () => request('GET', '/pilotage-bl/offer-mapping'),
    saveOfferMapping: (mapping) => request('PUT', '/pilotage-bl/offer-mapping', { mapping }),
    /** TJM (CHF/jour) d'une personne sur une affaire synolog — heures sans taux. */
    setAffaireRate: ({ affaireId, personId, tjm, unratedDays }) =>
      request('PUT', '/pilotage-bl/affaire-rate', { affaireId, personId, tjm, unratedDays }),
    /** Fiche synolog d'une affaire : TJM de l'affaire (CHF/j) et budget. */
    getAffaire: (affaireId) => request('GET', `/pilotage-bl/affaire/${encodeURIComponent(affaireId)}`),
    /** Mise à jour TJM de l'affaire / budget ; champ absent = inchangé, null = retiré. */
    updateAffaire: (affaireId, patch) => request('PATCH', `/pilotage-bl/affaire/${encodeURIComponent(affaireId)}`, patch),
  },

  // ---------- Indicateurs exécutifs (vue Direction, réservée canViewExec) ----------
  exec: {
    /** Synthèse exécutive : KPIs, banc list (couvert/sec), points chauds. */
    summary: ({ kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/forecast/exec-summary?${qs}`);
    },
    /** Tendance hebdo : série des snapshots des `weeks` dernières semaines. */
    trend: ({ weeks = 8 } = {}) => request('GET', `/forecast/exec-trend?weeks=${weeks}`),
    /** CA réalisé & capacité par (personne, mois) — panneau CA & TACE (#14). */
    revenue: ({ start, end }) =>
      request('GET', `/forecast/exec-revenue?${new URLSearchParams({ start, end })}`),
    /**
     * Affaires en alerte budget (source Synolog M2M, best-effort). Renvoie
     * `{ alerts: [] }` si le core/synolog est injoignable ou le scope absent.
     */
    budgetAlerts: () => request('GET', '/forecast/budget-alerts'),
  },

  // ---------- Contrôle Synolog (onglet Admin + Direction) ----------
  synologCheck: {
    /** Capacités serveur (ex. mailerConfigured) pour piloter l'UI. */
    config: () => request('GET', '/synolog-check/config'),

    /**
     * Contrôle des saisies sur une période PASSÉE.
     * @param {object} p
     * @param {'last-week'|'current-month'|'previous-month'|'custom'} p.kind
     * @param {string} [p.start] - 'YYYY-MM-DD' (custom)
     * @param {string} [p.end]   - 'YYYY-MM-DD' (custom)
     */
    timesheets: ({ kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/synolog-check/timesheets?${qs}`);
    },

    /**
     * Écarts saisies (Float ou pointages Synolog) vs imputations GESCOM.
     * @param {object} p
     * @param {'current-week'|'last-week'|'current-month'|'previous-month'|'custom'} p.kind
     * @param {boolean} [p.refresh] - force le rechargement du cache GESCOM
     */
    imputations: ({ kind, start, end, refresh } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      if (refresh) qs.set('refresh', '1');
      return request('GET', `/synolog-check/imputations?${qs}`);
    },

    /**
     * Écarts saisies vs imputations GESCOM à la maille **projet** (affaire).
     *
     * Complémentaire de `imputations` : ici on vérifie que les jours sont sur la
     * bonne affaire, pas qu'ils sont au bon volume.
     * @param {object} p
     * @param {'current-week'|'last-week'|'current-month'|'previous-month'|'custom'} p.kind
     * @param {number[]} [p.peopleIds] - restriction à un ou plusieurs collaborateurs
     * @param {boolean} [p.refresh] - force le rechargement du cache GESCOM
     */
    imputationProjects: ({ kind, start, end, peopleIds, refresh } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      if (peopleIds?.length) qs.set('peopleIds', peopleIds.join(','));
      if (refresh) qs.set('refresh', '1');
      return request('GET', `/synolog-check/imputations-projects?${qs}`);
    },

    /**
     * Fixe la correspondance projet Float → affaire Gescom (cas ambigu).
     *
     * ⚠️ Écrit dans la table de transcodification **partagée avec la synchro
     * Python** : la correspondance change ce qui sera réellement imputé dans
     * Gescom au prochain passage, ce n'est pas un réglage d'affichage.
     * Réservé aux admins.
     * @param {{ floatProjectName: string, gescomProjectId: string }} p
     */
    setTransco: ({ floatProjectName, gescomProjectId }) =>
      request('POST', '/synolog-check/transco', { floatProjectName, gescomProjectId }),

    /** Contrôle du renseignement des status journaliers (mêmes paramètres que timesheets). */
    statuses: ({ kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/synolog-check/statuses?${qs}`);
    },

    /** Time-offs Float NON validés sur la période, regroupés par manager. */
    pendingTimeoffs: ({ kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/synolog-check/pending-timeoffs?${qs}`);
    },

    /** Jours où un collaborateur a saisi > 8h sur Float (heures supplémentaires). */
    overtime: ({ kind, start, end } = {}) => {
      const qs = new URLSearchParams({ kind });
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      return request('GET', `/synolog-check/overtime?${qs}`);
    },

    /**
     * Envoie un mail de rappel à un consultant.
     * @param {object} input
     * @param {number} input.peopleId
     * @param {'timesheet'|'status'} [input.topic]
     * @param {string} input.kind  (+ start/end si custom)
     */
    remind: ({ peopleId, topic = 'timesheet', kind, start, end } = {}) =>
      request('POST', '/synolog-check/remind', {
        peopleId,
        topic,
        kind,
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      }),

    /**
     * Envoie au manager un mail récapitulant ses time-offs en attente sur la période.
     * @param {object} input
     * @param {string} input.managerKey - clé opaque retournée par /pending-timeoffs
     * @param {string} input.kind       - même valeur que pour la requête de lecture
     */
    remindManager: ({ managerKey, kind, start, end } = {}) =>
      request('POST', '/synolog-check/remind-manager', {
        managerKey,
        kind,
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      }),
  },

  // ---------- Contrôle de gestion (onglet adminOrExec) ----------
  controleGestion: {
    /** Liste des campagnes annuelles. */
    listCampaigns: () => request('GET', '/controle-gestion/campaigns'),

    /** Crée une campagne. */
    createCampaign: ({ year, label, defaultAcquisCp, hoursPerDay } = {}) =>
      request('POST', '/controle-gestion/campaigns', {
        year,
        label,
        ...(defaultAcquisCp != null ? { defaultAcquisCp } : {}),
        ...(hoursPerDay != null ? { hoursPerDay } : {}),
      }),

    /** Détail d'une campagne (+ état des 4 imports via `importsByKind`). */
    getCampaign: (id) => request('GET', `/controle-gestion/campaigns/${id}`),

    /** Met à jour une campagne (label / status / paramètres). */
    updateCampaign: (id, { label, status, defaultAcquisCp, hoursPerDay } = {}) => {
      const body = {};
      if (label !== undefined) body.label = label;
      if (status !== undefined) body.status = status;
      if (defaultAcquisCp !== undefined) body.defaultAcquisCp = defaultAcquisCp;
      if (hoursPerDay !== undefined) body.hoursPerDay = hoursPerDay;
      return request('PATCH', `/controle-gestion/campaigns/${id}`, body);
    },

    /** Supprime une campagne (DRAFT uniquement). */
    deleteCampaign: (id) => request('DELETE', `/controle-gestion/campaigns/${id}`),

    /**
     * Importe un CSV pour un volet de la campagne.
     * @param {string} id - campaignId
     * @param {'cjm'|'absences'|'cra'|'temps-partiels'} kind
     * @param {File} file
     */
    importCsv: (id, kind, file, { mode } = {}) =>
      upload(
        `/controle-gestion/campaigns/${id}/imports/${kind}`,
        file,
        'file',
        mode ? { mode } : {},
      ),

    /** Supprime un mois de CRA (gestion au compte-goutte). */
    deleteCraMonth: (id, month) =>
      request('DELETE', `/controle-gestion/campaigns/${id}/imports/cra/month/${month}`),

    /** Aperçu paginé des lignes importées d'un volet. */
    previewImport: (id, kind, { page = 1, pageSize = 50 } = {}) =>
      request('GET', `/controle-gestion/campaigns/${id}/imports/${kind}?page=${page}&pageSize=${pageSize}`),

    /** Paramètres CP (acquis / report N-1) par collaborateur. */
    listParams: (id) => request('GET', `/controle-gestion/campaigns/${id}/params`),

    /** Upsert d'un paramètre CP. `collabKey` passé en body (évite les [ ] en URL). */
    saveParam: (id, { collabKey, collabName, acquisCp, reportN1 } = {}) =>
      request('PUT', `/controle-gestion/campaigns/${id}/params`, {
        collabKey,
        ...(collabName !== undefined ? { collabName } : {}),
        ...(acquisCp !== undefined ? { acquisCp } : {}),
        ...(reportN1 !== undefined ? { reportN1 } : {}),
      }),

    /**
     * Override d'une ligne de congés (volet CP) : exclusion / nb de jours forcé.
     * excluded=false + daysOverride=null → réinitialise la ligne (calcul auto).
     * Le détail des lignes est fourni par compute().cp[].details.
     */
    saveAbsenceOverride: (id, { lineKey, excluded, daysOverride } = {}) =>
      request('PUT', `/controle-gestion/campaigns/${id}/absence-override`, {
        lineKey,
        ...(excluded !== undefined ? { excluded } : {}),
        ...(daysOverride !== undefined ? { daysOverride } : {}),
      }),

    /** Ventilation sans solde par mois. */
    listSansSolde: (id) => request('GET', `/controle-gestion/campaigns/${id}/sans-solde`),

    /** Upsert batch de la ventilation sans solde d'un collaborateur. */
    saveSansSolde: (id, { collabKey, months } = {}) =>
      request('PUT', `/controle-gestion/campaigns/${id}/sans-solde`, { collabKey, months }),

    /** Recalcul à la volée (4 volets + synthèse). */
    compute: (id) => request('GET', `/controle-gestion/campaigns/${id}/compute`),

    /** Fige le calcul + clôture la campagne. */
    snapshot: (id) => request('POST', `/controle-gestion/campaigns/${id}/snapshot`),

    /** URL d'export xlsx multi-feuilles (à ouvrir dans un nouvel onglet). */
    exportUrl: (id) => `${API_URL}/controle-gestion/campaigns/${id}/export`,

    /**
     * Refacturation SMILE — contrôle mensuel des factures refacturant les
     * collaborateurs FR (Saint-Étienne).
     *
     * ⚠ Référentiel autonome : Synosight ne connaît que les collaborateurs CH,
     * donc l'identité est le `people_id` Float, pas le `smileId` GESCOM.
     */
    smileRebill: {
      // -- Périmètre & tarifs --
      /** Collaborateurs référencés (CJM EUR + markup + opt-in périmètre). */
      listRates: () => request('GET', '/controle-gestion/smile-rebill/rates'),

      /** Personnes Float `@smile.fr` (hors `ext.`) pas encore référencées. */
      listCandidates: () => request('GET', '/controle-gestion/smile-rebill/rates/candidates'),

      createRate: (input) => request('POST', '/controle-gestion/smile-rebill/rates', input),
      updateRate: (id, input) => request('PUT', `/controle-gestion/smile-rebill/rates/${id}`, input),
      deleteRate: (id) => request('DELETE', `/controle-gestion/smile-rebill/rates/${id}`),

      // -- Structures productrices (Smile FR, Smile CH…) --
      /** Structures : devise, markup par défaut, source des jours (SYNOLOG | GESCOM). */
      listEntities: () => request('GET', '/controle-gestion/smile-rebill/entities'),
      updateEntity: (id, input) => request('PUT', `/controle-gestion/smile-rebill/entities/${id}`, input),

      // -- Périodes mensuelles --
      listPeriods: () => request('GET', '/controle-gestion/smile-rebill/periods'),
      createPeriod: ({ year, month, hoursPerDay } = {}) =>
        request('POST', '/controle-gestion/smile-rebill/periods', {
          year,
          month,
          ...(hoursPerDay != null ? { hoursPerDay } : {}),
        }),
      /**
       * Met à jour la période. `invoiceRef` / `invoiceTotal` portent le
       * rapprochement : SMILE facture un forfait mensuel sans détail par
       * personne, l'écart est donc global.
       */
      updatePeriod: (id, input) => request('PATCH', `/controle-gestion/smile-rebill/periods/${id}`, input),
      deletePeriod: (id) => request('DELETE', `/controle-gestion/smile-rebill/periods/${id}`),

      /**
       * Facture d'une structure pour la période (une par structure) :
       * `invoiceRef`, `amount` (dans la devise de la structure) et
       * `gescomProjectIds` (ProjectId Gescom INTERNES cités sur la facture).
       */
      saveInvoice: (id, entityId, input) =>
        request('PUT', `/controle-gestion/smile-rebill/periods/${id}/invoices/${entityId}`, input),

      // -- Tarifs applicables à une période (CJM + markup du mois) --
      /** Tarifs de la période ; `inherited: true` = hérité du référentiel. */
      listPeriodRates: (id) => request('GET', `/controle-gestion/smile-rebill/periods/${id}/rates`),

      /** Reprend les CJM/markups d'une période précédente (ou du référentiel). */
      copyRates: (id, { fromPeriodId, overwrite } = {}) =>
        request('POST', `/controle-gestion/smile-rebill/periods/${id}/rates/copy`, {
          ...(fromPeriodId ? { fromPeriodId } : {}),
          ...(overwrite !== undefined ? { overwrite } : {}),
        }),

      /** Fige ou corrige le tarif d'un collaborateur pour cette période. */
      savePeriodRate: (id, { rateId, cjmEur, markupCoef, cjmMarkupEur } = {}) =>
        request('PUT', `/controle-gestion/smile-rebill/periods/${id}/rates`, {
          rateId,
          cjmEur,
          ...(markupCoef !== undefined ? { markupCoef } : {}),
          ...(cjmMarkupEur !== undefined ? { cjmMarkupEur } : {}),
        }),

      /** Recharge les jours du mois — Synolog ou Gescom selon la structure (les overrides manuels survivent). */
      refresh: (id) => request('POST', `/controle-gestion/smile-rebill/periods/${id}/refresh`),

      /** Résultat : lignes, synthèse, rapprochement, alertes. */
      compute: (id) => request('GET', `/controle-gestion/smile-rebill/periods/${id}/compute`),

      /** Exclusion manuelle / nb de jours forcé sur une ligne. Renvoie le résultat recalculé. */
      saveLineOverride: (id, { lineKey, excludedManual, daysOverride } = {}) =>
        request('PUT', `/controle-gestion/smile-rebill/periods/${id}/line-override`, {
          lineKey,
          ...(excludedManual !== undefined ? { excludedManual } : {}),
          ...(daysOverride !== undefined ? { daysOverride } : {}),
        }),

      /**
       * Ajoute une ligne saisie à la main (jours absents des sources). Conservée
       * par les recalculs. Renvoie le résultat recalculé.
       */
      addManualLine: (id, { rateId, projectName, clientName, days } = {}) =>
        request('POST', `/controle-gestion/smile-rebill/periods/${id}/lines`, {
          rateId,
          projectName,
          ...(clientName ? { clientName } : {}),
          days,
        }),

      /** Supprime une ligne saisie à la main. Renvoie le résultat recalculé. */
      deleteManualLine: (id, lineKey) =>
        request('DELETE', `/controle-gestion/smile-rebill/periods/${id}/lines/${encodeURIComponent(lineKey)}`),

      /** Fige le résultat et clôture la période. */
      snapshot: (id) => request('POST', `/controle-gestion/smile-rebill/periods/${id}/snapshot`),

      exportUrl: (id) => `${API_URL}/controle-gestion/smile-rebill/periods/${id}/export`,
    },
  },

  // ---------- Onboarding (Admin + Direction + référent désigné) ----------
  onboarding: {
    // -- Templates (réservés Admin / Direction) --
    /** Liste des templates actifs (+ nb d'étapes et de parcours). */
    listTemplates: () => request('GET', '/onboarding/templates'),

    /** Détail d'un template avec ses étapes ordonnées. */
    getTemplate: (id) => request('GET', `/onboarding/templates/${id}`),

    /**
     * Crée un template.
     * @param {{ name: string, description?: string, active?: boolean,
     *   steps: Array<{ title, description?, category?, ownerType?, dueOffsetDays? }> }} input
     */
    createTemplate: (input) => request('POST', '/onboarding/templates', input),

    /** Crée le template standard Synotis pré-rempli (issu du processus d'accueil). */
    createStandardTemplate: () => request('POST', '/onboarding/templates/standard'),

    /** Remplace un template et ses étapes (n'impacte pas les parcours existants). */
    updateTemplate: (id, input) => request('PUT', `/onboarding/templates/${id}`, input),

    /** Soft delete d'un template. */
    deleteTemplate: (id) => request('DELETE', `/onboarding/templates/${id}`),

    /** Référents candidats (utilisateurs actifs) — pour assigner un parcours. */
    referentCandidates: () => request('GET', '/onboarding/referent-candidates'),

    // -- Parcours (scope référent appliqué côté API) --
    /** Liste des parcours (filtre statut optionnel) avec % d'avancement. */
    listJourneys: ({ status } = {}) => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request('GET', `/onboarding/journeys${qs}`);
    },

    /**
     * Instancie un parcours depuis un template (snapshot figé des étapes).
     * @param {{ templateId, personName, personEmail, startDate, referentId? }} input
     */
    createJourney: (input) => request('POST', '/onboarding/journeys', input),

    /** Détail d'un parcours (+ étapes + avancement). */
    getJourney: (id) => request('GET', `/onboarding/journeys/${id}`),

    /** Met à jour un parcours (statut / référent / date / identité). */
    updateJourney: (id, { status, referentId, startDate, personName, personEmail } = {}) => {
      const body = {};
      if (status !== undefined) body.status = status;
      if (referentId !== undefined) body.referentId = referentId;
      if (startDate !== undefined) body.startDate = startDate;
      if (personName !== undefined) body.personName = personName;
      if (personEmail !== undefined) body.personEmail = personEmail;
      return request('PATCH', `/onboarding/journeys/${id}`, body);
    },

    /** Supprime un parcours (Admin / Direction). */
    deleteJourney: (id) => request('DELETE', `/onboarding/journeys/${id}`),

    /** Met à jour une étape d'un parcours (statut TODO/DONE/NA + note). */
    updateStep: (journeyId, stepId, { status, note } = {}) => {
      const body = {};
      if (status !== undefined) body.status = status;
      if (note !== undefined) body.note = note;
      return request('PATCH', `/onboarding/journeys/${journeyId}/steps/${stepId}`, body);
    },
  },
};
